import { useSavedViewRequiredElement } from "@/features/saved-views/saved-view-context";
import { ProgressApiTracker } from "@/utils/progress-api-tracker";
import { LoadingScreen } from "@faro-lotv/flat-ui";
import { assert } from "@faro-lotv/foundation";
import { Stack } from "@mui/system";
import type { JSX } from "react";
import { Outlet, useParams } from "react-router-dom";
import { ProjectLoadingContextProvider } from "./project-loading-context";
import { useProjectInitialData } from "./use-project-initial-data";
import { useUpdateProjectMetadata } from "./use-project-metadata";
import {
  useTrackOpenProject,
  useTrackProjectViewedAuditEvent,
} from "./use-track-project-events";

/**
 * Wrap a page using a project from the ProjectAPI ensuring the initial data is loaded
 * and allowing efficient fetching on additional parts of the project.
 * Shows the full-screen `LoadingScreen` while loading the initial data.
 *
 * Needs to be used inside an already authenticated context and inside an `ApiClientContextProvider`
 * to have the proper token data to talk to the ProjectApi
 *
 * See also `ProjectAccessProvider` and its usage.
 *
 * @returns a context provider with everything needed to access and fetch more parts of the project
 */
export function ProjectProvider(): JSX.Element | null {
  const { projectId } = useParams();
  assert(projectId, "No project ID provided");

  const requiredElement = useSavedViewRequiredElement();

  const isMetadataReady = useUpdateProjectMetadata(projectId);
  const isProjectReady = useProjectInitialData(projectId, requiredElement);
  const isReady = isProjectReady && isMetadataReady;

  useTrackOpenProject(isProjectReady);
  useTrackProjectViewedAuditEvent(projectId, isProjectReady);

  return (
    <>
      <ProjectLoadingContextProvider>
        {isReady && <Outlet />}
        <ProgressApiTracker />
      </ProjectLoadingContextProvider>

      {!isReady && (
        <Stack
          direction="row"
          sx={{ width: "100%", height: "100%", justifyContent: "center" }}
        >
          <LoadingScreen />
        </Stack>
      )}
    </>
  );
}
